import React, { useState, useRef, useEffect } from "react";
import * as Yup from "yup";
import { useFormik } from "formik";
import { FaEye, FaTimes } from "react-icons/fa";
import TechnicalSupportModal from "../../components/TechnicalSupport/TechnicalSupportModal";
import { FiPlus } from "react-icons/fi";
import { IoMdClose } from "react-icons/io";

import toast from "react-hot-toast";
import SupportStatusCards from "../../components/TechnicalSupport/SupportStatusCards";
import { useSelector } from "react-redux";

import ErrorMessage from "../../components/ErrorMessage";
import axios from "axios";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useTranslation } from "react-i18next";
import useLockBodyScroll from "@/hooks/useLockBodyScroll";

const radioOptions = ["feedback", "suggestion", "bug", "report", "feature"];

interface Ticket {
  _id: string;
  userId: string;
  type: "feedback" | "suggestion" | "bug" | "report" | "feature";
  subject: string;
  message: string;
  attachments: string[];
  time: number;
  status: "pending" | "open" | "reviewing" | "approved" | "rejected";
}

function TechnicalSupport() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [needRefetch, setNeedRefetch] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<null | string>(null);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [attachments, setAttachments] = useState<File[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  useLockBodyScroll(isModalOpen);
  const modalRef = useRef<HTMLDivElement>(null);
  const token = useSelector(
    (state: { auth: { token: string } }) => state.auth.token,
  );
  const BASE_URL = import.meta.env.VITE_BASE_URL;
  const url = `${BASE_URL}/api/dashboard/tickets?locale=${lang}`;

  useEffect(() => {
    if (!token) return

    async function fetchTickets() {
      setIsLoading(true);
      setError(null);
      setNeedRefetch(false);

      try {
        const res = await axios.get(url, {
          headers: {
            Authorization: `${token}`,
          },
        });

        if (!res.data.success) {
          setError(res.data.message);
          setIsLoading(false);
          return;
        }

        setTickets(res.data.tickets);
        setIsLoading(false);
      } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
          setIsLoading(false);
          setError(error.response?.data?.error);
        } else {
          setError(
            t("technicalSupport.unexpectedError") as string,
          );
        }
      }
    }

    fetchTickets();
    // eslint-disable-next-line
  }, [lang, token, needRefetch]);

  const validationSchema = Yup.object({
    feedbackType: Yup.string().required(
      t("technicalSupport.feedbackTypeRequired") as string,
    ),
    subject: Yup.string()
      .min(
        2,
        t("technicalSupport.subjectMinLength") as string,
      )
      .required(t("technicalSupport.subjectRequired") as string),
    message: Yup.string()
      .min(
        3,
        t("technicalSupport.messageMinLength") as string,
      )
      .required(t("technicalSupport.messageRequired") as string),
  });

  const formik = useFormik({
    initialValues: {
      feedbackType: "",
      subject: "",
      message: "",
    },
    validationSchema,
    onSubmit: (values) => {

      const { feedbackType, subject, message } = values;

      const sendTechnicalSupportRequest = async () => {
        const LoadingToast = toast.loading(
          t("technicalSupport.sending"
          ) as string,
        );

        // When sending multple files need to use formdata Object
        const body = new FormData();
        body.set("type", feedbackType);
        body.set("subject", subject);
        body.set("message", message);
        attachments.forEach((attachment) => {
          body.append("attachment", attachment);
        });

        try {
          const response = await axios.postForm(url, body, {
            headers: {
              Authorization: `${token}`,
              "Content-Type": "multipart/form-data",
            },
          });


          if (!response.data.success) {
            toast.error(response.data.error);
            return;
          }

          toast.success(
            t(
              "technicalSupport.requestSuccess"
            ) as string,
          );

          setIsModalOpen(false);
          formik.resetForm();
          setNeedRefetch(true);
        } catch (error: unknown) {
          if (axios.isAxiosError(error)) {
            const errorMessage =
              error.response?.data?.error ||
              (t(
                "technicalSupport.failedToSend"
              ) as string);
            toast.error(errorMessage);
          } else {
            toast.error(
              t(
                "technicalSupport.unexpectedError"
              ) as string,
            );
          }
          console.error(
            t(
              "technicalSupport.errorSending"
            ) as string,
          );
        } finally {
          toast.dismiss(LoadingToast);
        }
      };

      sendTechnicalSupportRequest();
    },
  });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length <= 5) {
      setAttachments(Array.from(e.target.files));
    } else {
      toast.error(
        t(
          "technicalSupport.uploadLimit"
        ) as string,
      );
    }
  };

  const removeAttachment = (index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const openImagePreview = (file: File) => {
    const reader = new FileReader();
    reader.onload = () => {
      setSelectedImage(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const closeModal = () => {
    setSelectedImage(null);
  };

  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);
  };

  const handleClickOutside = (e: MouseEvent) => {
    if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
      setIsModalOpen(false);
    }
  };

  useEffect(() => {
    if (isModalOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isModalOpen]);

  if (isLoading) return <>
    <div className="grid grid-cols-1 gap-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 mt-20">
      <Skeleton className="h-[400px] dark:bg-slate-600 dark:bg-opacity-35  " />
      <Skeleton className="h-[400px] dark:bg-slate-600 dark:bg-opacity-35  " />
      <Skeleton className="h-[400px] dark:bg-slate-600 dark:bg-opacity-35  " />
      <Skeleton className="h-[400px] dark:bg-slate-600 dark:bg-opacity-35  " />
      <Skeleton className="h-[400px] dark:bg-slate-600 dark:bg-opacity-35  " />
      <Skeleton className="h-[400px] dark:bg-slate-600 dark:bg-opacity-35  " />
    </div>
  </>;
  if (error) return <ErrorMessage msg={error} />;

  return (
    <div className="flex flex-col justify-center gap-2">
      <div className="flex w-full items-center justify-between gap-1">
        <h2 className="col-span-2 self-start py-1 text-[30px] capitalize md:col-span-3 md:text-[40px] xl:col-span-4">
          {t("technicalSupport.technicalSupport")}
        </h2>
        <button
          type="button"
          className={`${t("mt-2", "mt-0")} flex aspect-square h-10 cursor-pointer flex-col items-center justify-center rounded-md border-l bg-sky-300 text-xl text-sky-900 hover:bg-sky-400`}
          onClick={toggleModal}
        >
          <FiPlus />
        </button>
      </div>

      {!!tickets.length && <h2 className="col-span-2 py-1 text-[20px] capitalize md:col-span-3 md:text-[30px] xl:col-span-4">
        {t("technicalSupport.supportStatus")}
      </h2>}

      {!tickets.length && <>
        <Alert className="bg-skyblue dark:bg-skyblue text-sidebar dark:text-sidebar border-none dark:border-none">
          <AlertTitle className="text-xl text-center"> {t("technicalSupport.noTicketsFound")}</AlertTitle>
          <AlertDescription className="text-center my-5 font-semibold">
            {t("technicalSupport.noTickets")}
          </AlertDescription>
        </Alert>
      </>}

      {/* Support Status Cards Grid container */}
      <div className="grid grid-cols-1 gap-10 sm:grid-cols-2 sm:gap-5 lg:grid-cols-3 xl:grid-cols-4">
        {tickets.map((ticket, i) => (
          <SupportStatusCards
            key={i}
            message={ticket.message}
            status={ticket.status}
            subject={ticket.subject}
            type={ticket.type}
            imgSrc={ticket.attachments[0]}
          />
        ))}
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50">
          <div
            ref={modalRef}
            className="flex max-h-[75vh] w-[95%] max-w-[800px] flex-col rounded-lg border border-gray-300 bg-white p-6"
          >
            {/* Modal Header */}
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-xl font-semibold">
                {t("technicalSupport.submitTicket")}
              </h3>
              <button onClick={toggleModal} className="text-xl text-red-600">
                <IoMdClose size={24} className="text-red-600" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="thin-scrollbar mb-4 flex-1 overflow-y-auto">
              <form
                onSubmit={formik.handleSubmit}
                id="support-form"
                className="flex flex-col gap-6 px-2"
              >
                {/* Feedback Type */}
                <div>
                  <label className="block text-lg font-medium">
                    {t("technicalSupport.feedbackType")}
                  </label>
                  <div className="mt-2 flex flex-col gap-4 md:flex-row">
                    {radioOptions.map((type) => (
                      <label
                        key={type}
                        className="flex cursor-pointer items-center gap-2"
                      >
                        <input
                          type="radio"
                          name="feedbackType"
                          value={type}
                          onChange={formik.handleChange}
                          className="accent-sidebar"
                        />
                        <span>
                          {t(`technicalSupport.types.${type}`)}
                        </span>
                      </label>
                    ))}
                  </div>
                  {formik.touched.feedbackType &&
                    formik.errors.feedbackType && (
                      <p className="mt-1 text-sm text-red-600">
                        {formik.errors.feedbackType}
                      </p>
                    )}
                </div>

                {/* Subject */}
                <div>
                  <label
                    htmlFor="subject"
                    className="block text-sm font-semibold text-gray-700"
                  >
                    {t("technicalSupport.messageSubject")}
                  </label>
                  <input
                    id="subject"
                    name="subject"
                    type="text"
                    value={formik.values.subject}
                    onChange={formik.handleChange}
                    className="mt-1 w-full rounded border p-2"
                  />
                  {formik.touched.subject && formik.errors.subject && (
                    <p className="mt-1 text-sm text-red-600">
                      {formik.errors.subject}
                    </p>
                  )}
                </div>

                {/* Message */}
                <div>
                  <label
                    htmlFor="message"
                    className="block text-sm font-semibold text-gray-700"
                  >
                    {t("technicalSupport.messageBody")}
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={4}
                    value={formik.values.message}
                    onChange={formik.handleChange}
                    className="mt-1 max-h-[200px] w-full rounded border p-2"
                  ></textarea>
                  {formik.touched.message && formik.errors.message && (
                    <p className="mt-1 text-sm text-red-600">
                      {formik.errors.message}
                    </p>
                  )}
                </div>

                {/* File Attachments */}
                <div>
                  <label
                    htmlFor="attachments"
                    className="block text-sm font-semibold text-gray-700"
                  >
                    {t("technicalSupport.addAttachments")}
                  </label>
                  <div className="mt-1 rounded border p-4">
                    <input
                      id="attachments"
                      name="attachments"
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleFileChange}
                      className="hidden"
                    />
                    <label
                      htmlFor="attachments"
                      className="block w-full cursor-pointer rounded bg-sidebar py-2 text-center font-semibold text-skyblue"
                    >
                      {t("technicalSupport.uploadFiles")}
                    </label>
                    <ul className="mt-2 space-y-1">
                      {attachments.map((file, index) => (
                        <li
                          key={index}
                          className="flex items-center justify-between rounded bg-gray-100 p-2"
                        >
                          <span>{file.name}</span>
                          <div className="flex items-center space-x-2">
                            <button
                              type="button"
                              className="text-blue-600"
                              onClick={() => openImagePreview(file)}
                            >
                              <FaEye size={19} className="text-sidebar" />
                            </button>
                            <button
                              type="button"
                              className="text-red-600"
                              onClick={() => removeAttachment(index)}
                            >
                              <FaTimes size={19} />
                            </button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </form>
            </div>

            {/* Image Preview Modal */}
            {selectedImage && (
              <TechnicalSupportModal
                imageSrc={selectedImage}
                onClose={closeModal}
              />
            )}

            {/* Modal Footer */}
            <div className="flex justify-end">
              <button
                form="support-form"
                type="submit"
                className="w-fit self-end rounded-md bg-sidebar px-10 py-2 font-semibold text-skyblue transition"
              >
                {t("technicalSupport.submitTicket")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TechnicalSupport;
